import { useState } from 'react';
import { X, Image, FileText, Target, DollarSign, AtSign, Globe, Lock, Users as UsersIcon, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { addItem } from '@/utils/localStore';

const postTypes = [
  { key: 'update', label: 'Update', icon: FileText },
  { key: 'impact_report', label: 'Impact Report', icon: Target },
  { key: 'milestone', label: 'Milestone', icon: Target },
  { key: 'fundraiser', label: 'Fundraiser', icon: DollarSign },
];

const causeOptions = ['Education', 'Healthcare', 'Environment', 'Hunger Relief', 'Housing', 'Disaster Relief', 'Youth Dev', 'Animal Welfare'];
const privacyOptions = [
  { key: 'public', label: 'Public', icon: Globe },
  { key: 'connections', label: 'Connections Only', icon: UsersIcon },
  { key: 'org', label: 'Organization Only', icon: Lock },
];

const mockImages = [
  'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop',
  'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=600&h=400&fit=crop',
];

export default function CreatePostModal({ open, onClose, onPostCreated }) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [media, setMedia] = useState([]);
  const [causes, setCauses] = useState([]);
  const [privacy, setPrivacy] = useState('public');
  const [posting, setPosting] = useState(false);

  if (!open) return null;

  const handleAddMedia = () => {
    const img = mockImages[media.length % mockImages.length];
    setMedia([...media, img]);
  };

  const handleRemoveMedia = (idx) => setMedia(media.filter((_, i) => i !== idx));

  const handlePost = async () => {
    if (!content.trim() && media.length === 0) return;
    setPosting(true);

    const postData = {
      authorId: user?.username || 'user',
      authorName: user?.name || 'You',
      authorAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username || 'user'}`,
      authorType: user?.type || 'donor',
      content,
      images: media,
      postType,
      causeTags: causes,
      privacy,
    };

    try {
      const saved = addItem('user_posts', {
        ...postData,
        type: 'ngo_update',
        authorVerified: true,
        timestamp: new Date().toISOString(),
        reactions: { celebrates: 0, amplifies: 0, discusses: 0, verifies: 0, funds: 0 },
        commentsCount: 0,
      });

      // Build a local feed-compatible post object
      const newPost = {
        id: saved.id,
        type: 'ngo_update',
        authorId: postData.authorId,
        authorType: postData.authorType,
        authorName: postData.authorName,
        authorAvatar: postData.authorAvatar,
        authorVerified: true,
        content,
        images: media,
        metrics: postType === 'milestone' ? { label: 'New milestone posted', verified: true } : null,
        reactions: { celebrates: 0, amplifies: 0, discusses: 0, verifies: 0, funds: 0 },
        commentsCount: 0,
        timestamp: saved.createdAt,
        txHash: '0x' + Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join(''),
        donorCount: 0,
        donorAvatars: [],
        ngoSlug: '',
        projectSlug: '',
      };

      onPostCreated(newPost);
      toast.success('Post published!');
      setContent('');
      setMedia([]);
      setCauses([]);
      setPosting(false);
      onClose();
    } catch (_) {
      toast.error('Failed to publish post');
      setPosting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-black/40" onClick={onClose}>
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} className="w-full max-w-lg bg-white rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="create-post-modal">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900">Create Post</h2>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100" data-testid="close-create-modal"><X className="w-5 h-5 text-slate-400" /></button>
          </div>

          <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center text-white text-sm font-bold">{user?.initials}</div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Globe className="w-3 h-3" />
                  <select value={privacy} onChange={e => setPrivacy(e.target.value)} className="bg-transparent text-xs text-slate-500 outline-none cursor-pointer" data-testid="privacy-select">
                    {privacyOptions.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-1">
              {postTypes.map(t => (
                <button key={t.key} onClick={() => setPostType(t.key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${postType === t.key ? 'bg-[#7C3AED] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`type-${t.key}`}>
                  <t.icon className="w-3.5 h-3.5" />{t.label}
                </button>
              ))}
            </div>

            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="What impact are you making today?"
              className="w-full min-h-[120px] text-sm text-slate-900 placeholder-slate-400 resize-none outline-none"
              data-testid="post-content-input"
            />

            {media.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {media.map((img, i) => (
                  <div key={img + i} className="relative rounded-lg overflow-hidden">
                    <img src={img} alt="" className="w-full h-32 object-cover" />
                    <button onClick={() => handleRemoveMedia(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Add cause tags</p>
              <div className="flex flex-wrap gap-1.5">
                {causeOptions.map(c => (
                  <button key={c} onClick={() => setCauses(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${causes.includes(c) ? 'bg-[#7C3AED] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`cause-tag-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <div className="flex items-center gap-1">
              <button onClick={handleAddMedia} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-500" data-testid="add-media-btn"><Image className="w-5 h-5" /></button>
              <button className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 text-slate-500" data-testid="add-mention-btn"><AtSign className="w-5 h-5" /></button>
            </div>
            <Button onClick={handlePost} disabled={posting || (!content.trim() && media.length === 0)} className="btn-primary rounded-lg px-5 h-9 text-sm font-semibold disabled:opacity-50" data-testid="submit-post-btn">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
